import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Select,
  MenuItem,
} from '@mui/material';
import { getPartnersList, createPartnersApi } from '../services/api';
import type { PartnerRecord, PartnerListResponse } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const INSTITUTION_TYPE_LABELS: Record<string, string> = {
  university: 'University',
  college: 'College',
  community_college: 'Community College',
  technical_school: 'Technical School',
  'Program::DirectToConsumerProgram': 'Direct to Consumer',
  'Program::TierOneProgram': 'Tier 1',
  'Program::TierTwoProgram': 'Tier 2',
  'Program::TierThreeProgram': 'Tier 3',
  'Program::EmployerProgram': 'Employer',
};

function StatusChip({ status }: { status: string }) {
  const isActive = status === 'active';
  return (
    <Chip
      label={isActive ? 'Active' : 'Inactive'}
      size="small"
      sx={{
        bgcolor: isActive ? '#E8F5E9' : '#F5F5F5',
        color: isActive ? '#2E7D32' : '#616161',
        fontWeight: 600,
        fontSize: '0.75rem',
      }}
    />
  );
}

export function Partners(): React.ReactElement {
  const navigate = useNavigate();
  const { idToken, roles, partnerId } = useAuth();

  // Non-admin users with a partnerId are redirected to their own partner detail page
  if (!roles.includes('sophia_admin') && partnerId != null) {
    return <Navigate to={`/partners/${partnerId}`} replace />;
  }
  const partnersClient = useMemo(() => createPartnersApi(idToken), [idToken]);

  const [items, setItems] = useState<(PartnerRecord & { user_count: number })[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);

  const fetchPartners = useCallback(async () => {
    if (!idToken) return;
    try {
      setLoading(true);
      setError(null);
      const data: PartnerListResponse = await getPartnersList(
        {
          search: search || undefined,
          status: statusFilter,
          page: page + 1,
          per_page: rowsPerPage,
        },
        partnersClient
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('[Partners] Failed to fetch:', err);
      setError(err instanceof Error ? err.message : 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [idToken, search, statusFilter, page, rowsPerPage, partnersClient]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const isEmpty = !loading && items.length === 0 && !search && statusFilter === 'all';

  return (
    <>
      {/* Header band */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ pt: 4, pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h1" gutterBottom>
                Partners
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage partner institutions
              </Typography>
            </Box>
            {/* Create Partner is disabled — partners are created from the LMS */}
            {/* <Button variant="contained" onClick={handleCreateOpen} sx={{ px: 3 }}>
              Create Partner
            </Button> */}
          </Box>

          {/* Filter bar */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
            <TextField
              size="small"
              placeholder="Search by institution name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <i
                      className="fa-thin fa-magnifying-glass"
                      style={{ color: 'rgba(0,0,0,0.54)', fontSize: 18 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <i className="fa-thin fa-xmark" style={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={loading}
              sx={{ height: 40 }}
            >
              Search
            </Button>

            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </Stack>
        </Container>
      </Box>

      {/* Content area */}
      <Box sx={{ bgcolor: 'background.paper', flex: 1 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {isEmpty ? (
            /* Empty state */
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <Box sx={{ mb: 2 }}>
                <i
                  className="fa-thin fa-building-columns"
                  style={{ fontSize: 48, color: '#9e9e9e' }}
                />
              </Box>
              <Typography variant="h5" gutterBottom>
                No partners yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No partner institutions found
              </Typography>
              {/* Create Partner is disabled — partners are created from the LMS */}
              {/* <Button variant="contained" onClick={handleCreateOpen}>
                Create Partner
              </Button> */}
            </Box>
          ) : (
            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Partner</TableCell>
                      <TableCell align="right"># of Students</TableCell>
                      <TableCell align="right"># of Courses</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Subdomain</TableCell>
                      <TableCell>Promotion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={32} />
                        </TableCell>
                      </TableRow>
                    ) : items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">
                            No partners match your filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      items.map((p: any) => {
                        const subdomain = p.subdomain ? `${p.subdomain}.sophia.org` : null;
                        return (
                          <TableRow
                            key={p.partner_id}
                            hover
                            onClick={() => navigate(`/partners/${p.partner_id}`)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'primary.main',
                                  fontWeight: 600,
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {p.institution_name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {(p.user_count ?? 0).toLocaleString()}
                            </TableCell>
                            <TableCell align="right">{p.course_count ?? 0}</TableCell>
                            <TableCell>
                              {p.institution_type
                                ? INSTITUTION_TYPE_LABELS[p.institution_type] || p.institution_type
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <StatusChip status={p.status} />
                            </TableCell>
                            <TableCell>
                              {subdomain ? (
                                <Typography
                                  component="a"
                                  variant="body2"
                                  href={`https://${subdomain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                  sx={{
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  {subdomain}
                                </Typography>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  p.promotional_communications_enabled ? 'Enabled' : 'Disabled'
                                }
                                size="small"
                                sx={{
                                  bgcolor: p.promotional_communications_enabled
                                    ? '#E8F5E9'
                                    : '#F5F5F5',
                                  color: p.promotional_communications_enabled
                                    ? '#2E7D32'
                                    : '#616161',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={() => {}}
                rowsPerPageOptions={[50]}
              />
            </Paper>
          )}
        </Container>
      </Box>
    </>
  );
}
